import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, CheckIcon, StoreIcon, VideoIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { DeleteButton } from '../ui/DeleteButton';
import { CustomerTimeChoice } from './CustomerTimeChoice';
import { Spinner } from '../../ui/Spinner';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnPrimary, btnSecondary } from '../ui/styles';
import { useNow } from '../../hooks/useNow';
import { hasStarted } from '../../core/rules/agenda';
import { dayLong } from '../../core/format/dates';
import { ADMIN_BASE } from '../../utils/hosts';
import type { Appointment } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';

/** Eén afspraak in het dossier, met bevestigen, afronden, verzetten en annuleren. */
export function AppointmentRow({
  appt,
  ctl
}: {
  appt: Appointment;
  ctl: CustomerFileControl;
}) {
  const {
    index
  } = useAdmin();
  const [picking, setPicking] = useState(false);
  const t = index.treatmentBySlug(appt.treatmentSlug);
  const course = ctl.file.account.course;
  // Zelfde moment als in de agenda: afronden kan vanaf de begintijd, niet al 's ochtends.
  const now = useNow();
  const agendaHref = `${ADMIN_BASE}/agenda?datum=${appt.date}&afspraak=${encodeURIComponent(appt.id)}`;
  const Icon = appt.kind === 'video' ? VideoIcon : StoreIcon;
  // Tijdens het bewaren staan alle knoppen van deze afspraak uit: geen tweede "Afronden".
  const busy = ctl.isBusy(appt.id);
  return <li className="px-4 py-4 sm:px-5" data-id="element-1048" data-mp="1niuucgq0gvi6">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3" data-id="element-1049" data-mp="1nm0i9smwlrcb">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sand text-ink" aria-hidden="true" data-id="element-1050" data-mp="1skbu28wrebx6">
          <Icon className="h-4 w-4" strokeWidth={1.5} data-id="element-1051" data-mp="1wihwj4qke5ca" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-1052" data-mp="3ubow8q7ce5pj">
          <span className="flex flex-wrap items-center gap-2" data-id="element-1053" data-mp="3ucq9n1btz4af">
            <span className="text-[14px] text-ink" data-id="element-1054" data-mp="2735mklthzuy">
              {t?.name ?? appt.treatmentSlug}
              <span className="sr-only" data-id="element-1055" data-mp="15i0i2n3da7sa">{appt.kind === 'video' ? ' (videogesprek)' : ' (in de salon)'}</span>
            </span>
            {appt.status === 'aanvraag' ? <Badge tone="accent" data-id="element-1056" data-mp="1eif4jps2hsxd">{appt.request === 'verzet' ? 'Wil verzetten' : 'Aanvraag'}</Badge> : <Badge tone="soft" data-id="element-1057" data-mp="1afsadi7xbvx0">Bevestigd</Badge>}
          </span>
          <span className="mt-0.5 block text-[13px] capitalize text-muted" data-id="element-1058" data-mp="1ofkuuvsuxfwj">
            {dayLong(appt.date)} om {appt.time}
            {appt.session && course ? ` · sessie ${appt.session} van ${course.total}` : ''}
          </span>
        </span>
      </div>

      {picking ? <div className="mt-3 sm:ml-[3.25rem]" data-id="element-1059" data-mp="1t7bddp1y79z1">
          <CustomerTimeChoice durationMinutes={t?.durationMinutes ?? 60} ignoreId={appt.id} confirmLabel={when => `Voorstellen: ${when}`} busy={busy} onCancel={() => setPicking(false)} onConfirm={(date, time) => {
        void ctl.proposeAppointment(appt.id, date, time).then(ok => ok && setPicking(false));
      }} data-id="element-1060" data-mp="1t7l1f73fn723" />
        </div> : <div className="mt-3 flex flex-wrap items-center gap-2 sm:ml-[3.25rem]" data-id="element-1061" data-mp="2csv158cxs3wx">
          {appt.status === 'aanvraag' && <button type="button" onClick={() => ctl.confirmAppointment(appt.id)} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-1062" data-mp="2gtoo9zik1eor">
              {busy ? <Spinner data-id="element-1063" data-mp="230yrr15egrde" /> : <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1064" data-mp="2318kuxlqrenb" />}
              Bevestigen
            </button>}
          {/* Afronden op één plek: in de agenda, waar ook gebruikte producten en advies worden vastgelegd. */}
          {appt.status === 'bevestigd' && hasStarted(appt, now) ? <Link to={agendaHref} className={btnPrimary} data-id="element-1065" data-mp="1k3nrpeby1tww">
              <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1066" data-mp="1k6aayb875xuy" />
              Afronden in agenda
            </Link> : <Link to={agendaHref} className={btnGhost} data-id="element-1067" data-mp="1h2udvansp5jq">
              <CalendarDaysIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1068" data-mp="1h4p3h2a0hlu5" />
              In agenda
            </Link>}
          <button type="button" onClick={() => setPicking(true)} disabled={busy} className={btnSecondary} data-id="element-1069" data-mp="17bzp9zn2ttv4">
            {appt.status === 'aanvraag' ? 'Ander tijdstip' : 'Verzetten'}
          </button>
          <DeleteButton label="Annuleren" warning="Klant krijgt een bericht" onConfirm={() => ctl.removeAppointment(appt.id)} data-id="element-1070" data-mp="yccz69zssahd" />
        </div>}
    </li>;
}